import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

interface StageDistributionProps {
  byStage: {
    on_deck?: number;
    at_bat?: number;
    on_base?: number;
    scoring_position?: number;
    hall_of_fame?: number;
    bench?: number;
  };
}

// Baseball stage colors matching the theme
const STAGE_COLORS: Record<string, string> = {
  on_deck: '#8B5CF6',      // Purple - warming up
  at_bat: '#3B82F6',       // Blue - active engagement
  on_base: '#10B981',      // Green - in conversations
  scoring_position: '#F59E0B', // Amber - under contract
  hall_of_fame: '#EF4444', // Red - closed deals
  bench: '#6B7280',        // Gray - inactive
};

const STAGE_LABELS: Record<string, string> = {
  on_deck: 'On Deck',
  at_bat: 'At Bat',
  on_base: 'On Base',
  scoring_position: 'Scoring Position',
  hall_of_fame: 'Hall of Fame',
  bench: 'Bench',
};

const STAGE_EMOJIS: Record<string, string> = {
  on_deck: '⚾',
  at_bat: '⚾',
  on_base: '🏃',
  scoring_position: '🔥',
  hall_of_fame: '🏆',
  bench: '💤',
};

export default function StageDistributionChart({ byStage }: StageDistributionProps) {
  // Transform data for recharts
  const data = Object.entries(byStage || {})
    .filter(([_, value]) => value && value > 0) // Only show stages with contacts
    .map(([stage, value]) => ({
      name: STAGE_LABELS[stage] || stage,
      value: value || 0,
      stage,
      emoji: STAGE_EMOJIS[stage] || '',
    }));

  // If no data, show empty state
  if (data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Pipeline Distribution</h3>
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">No contacts to display</p>
        </div>
      </div>
    );
  }

  // Calculate total
  const total = data.reduce((sum, item) => sum + item.value, 0);

  // Custom label to show percentage
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
    const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);

    return percent > 0.05 ? ( // Only show if > 5%
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        className="text-sm font-semibold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    ) : null;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Pipeline Distribution</h3>

      <div className="h-80 w-full min-w-[400px]">
        <ResponsiveContainer width="100%" height="100%" debounce={100}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius={110}
              fill="#8884d8"
              dataKey="value"
            >
              {data.map((entry) => (
                <Cell key={`cell-${entry.stage}`} fill={STAGE_COLORS[entry.stage]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
              }}
              formatter={(value: number | undefined) => [`${value || 0} contacts`, 'Count']}
              labelFormatter={(label) => {
                const item = data.find((d) => d.name === label);
                return item ? `${item.emoji} ${label}` : label;
              }}
            />
            <Legend
              verticalAlign="bottom"
              height={36}
              formatter={(value) => {
                const item = data.find((d) => d.name === value);
                const percentage = total > 0 ? (((item?.value || 0) / total) * 100).toFixed(0) : 0;
                return `${item?.emoji || ''} ${value} (${item?.value || 0}, ${percentage}%)`;
              }}
              wrapperStyle={{
                paddingTop: '20px',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
